import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

/**
 * Decoración estacional global para páginas públicas.
 *   - Lee el tema activo desde /api/public/seasonal-theme (lo controla admin).
 *   - Renderiza decoraciones flotantes (emojis cayendo), banner superior y
 *     un overlay sutil de color para dar ambiente.
 *   - NO se renderiza en rutas de productos coreanos (/coreanos, /coreanosenvios).
 *   - Pointer-events none: nunca bloquea interacción con el contenido.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEMES = {
  halloween: {
    label: 'Halloween',
    emojis: ['🎃', '🦇', '👻', '🕸️', '🕷️', '🍬'],
    banner: '🎃 Especial Halloween — Dulce o truco',
    bannerBg: 'linear-gradient(90deg, #0a0a0a 0%, #4a1a00 50%, #ff6a00 100%)',
    bannerText: '#ffd48a',
    tint: 'rgba(255, 106, 0, 0.06)',
  },
  christmas: {
    label: 'Navidad',
    emojis: ['🎄', '🎅', '❄️', '⛄', '🎁', '✨'],
    banner: '🎄 Especial Navidad — Felices fiestas',
    bannerBg: 'linear-gradient(90deg, #0b3b14 0%, #c0392b 100%)',
    bannerText: '#fff8dc',
    tint: 'rgba(192, 57, 43, 0.05)',
  },
  day_of_dead: {
    label: 'Día de Muertos',
    emojis: ['💀', '🌼', '🕯️', '🥀', '🦋', '🎭'],
    banner: '💀 Día de Muertos — Honramos a los que se adelantaron',
    bannerBg: 'linear-gradient(90deg, #2b0a3a 0%, #d35400 100%)',
    bannerText: '#ffe6a7',
    tint: 'rgba(211, 84, 0, 0.06)',
  },
  mothers_day: {
    label: 'Día de la Madre',
    emojis: ['🌸', '🌷', '💐', '💗', '🌹', '🎀'],
    banner: '💐 Día de la Madre — Consiéntela con un regalo',
    bannerBg: 'linear-gradient(90deg, #ff6aa0 0%, #ffb3cc 100%)',
    bannerText: '#4a0033',
    tint: 'rgba(255, 106, 160, 0.07)',
  },
  valentines: {
    label: 'San Valentín',
    emojis: ['💕', '🌹', '💘', '💝', '💌', '😍'],
    banner: '💘 San Valentín — Sorprende a quien amas',
    bannerBg: 'linear-gradient(90deg, #b00020 0%, #ff3a6e 100%)',
    bannerText: '#fff0f3',
    tint: 'rgba(255, 58, 110, 0.07)',
  },
  kings: {
    label: 'Día de Reyes',
    emojis: ['👑', '🐫', '⭐', '🎁', '🌟', '🧁'],
    banner: '👑 Día de Reyes — ¡Que los Reyes te traigan todo!',
    bannerBg: 'linear-gradient(90deg, #0a1a4a 0%, #c9a227 100%)',
    bannerText: '#fff8dc',
    tint: 'rgba(201, 162, 39, 0.06)',
  },
};

const SKIP_PREFIXES = ['/coreanos', '/coreanosenvios', '/korean'];

function FallingEmojis({ emojis }) {
  // 24 piezas con posiciones y delays estables → se memoriza para no re-barajar
  const items = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 24; i += 1) {
      arr.push({
        key: `fx-${i}`,
        emoji: emojis[i % emojis.length],
        left: Math.random() * 100,
        delay: Math.random() * 8,
        duration: 10 + Math.random() * 10,
        size: 18 + Math.random() * 22,
        drift: (Math.random() - 0.5) * 60,
        rotate: (Math.random() - 0.5) * 60,
      });
    }
    return arr;
  }, [emojis]);

  return (
    <div
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 50 }}
      data-testid="seasonal-fx-layer"
      aria-hidden="true"
    >
      {items.map((it) => (
        <span
          key={it.key}
          style={{
            position: 'absolute',
            top: '-10%',
            left: `${it.left}%`,
            fontSize: `${it.size}px`,
            opacity: 0.72,
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
            animation: `seasonalFall ${it.duration}s linear ${it.delay}s infinite`,
            '--drift': `${it.drift}px`,
            '--rot': `${it.rotate}deg`,
            willChange: 'transform, opacity',
          }}
        >
          {it.emoji}
        </span>
      ))}
    </div>
  );
}

export default function SeasonalDecoration() {
  const [theme, setTheme] = useState(null);
  const location = useLocation();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/seasonal-theme`);
        if (!cancel) setTheme(data?.active || null);
      } catch (_) {
        if (!cancel) setTheme(null);
      }
    })();
    // Revalida cada 60s por si admin activa un tema mientras el cliente está abierto
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/public/seasonal-theme`);
        if (!cancel) setTheme(data?.active || null);
      } catch (_) { /* noop */ }
    }, 60000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // Skip productos coreanos (ambas rutas)
  const skip = useMemo(() => {
    const p = location?.pathname || '';
    return SKIP_PREFIXES.some((pref) => p === pref || p.startsWith(`${pref}/`) || p === pref.replace(/^\//, ''));
  }, [location]);

  if (!theme || skip) return null;
  const cfg = THEMES[theme];
  if (!cfg) return null;

  return (
    <>
      {/* Keyframes de la lluvia de emojis + animación del banner */}
      <style>{`
        @keyframes seasonalFall {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
          8%   { opacity: 0.72; }
          92%  { opacity: 0.72; }
          100% { transform: translate3d(var(--drift, 0), 115vh, 0) rotate(var(--rot, 0deg)); opacity: 0; }
        }
        @keyframes seasonalBannerSlide {
          0%   { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>

      {/* Tinte global sutil */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ background: cfg.tint, zIndex: 1 }}
        aria-hidden="true"
      />

      {/* Lluvia de emojis */}
      <FallingEmojis emojis={cfg.emojis} />

      {/* Banner superior fijo (pasa por detrás del menú hamburguesa) */}
      <div
        className="fixed left-0 right-0 overflow-hidden pointer-events-none"
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 0px)',
          height: 28,
          background: cfg.bannerBg,
          color: cfg.bannerText,
          zIndex: 60,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: '0.03em',
        }}
        data-testid={`seasonal-banner-${theme}`}
        aria-hidden="true"
      >
        <div
          style={{
            whiteSpace: 'nowrap',
            display: 'inline-block',
            paddingLeft: '100%',
            animation: 'seasonalBannerSlide 22s linear infinite',
            lineHeight: '28px',
          }}
        >
          {`${cfg.banner}  •  ${cfg.banner}  •  ${cfg.banner}  •  ${cfg.banner}`}
        </div>
      </div>
    </>
  );
}
